import { useState } from 'react';
import { Check, GripVertical, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CostPriceCategory, updateCostPriceCategory } from '../../services/costPrice/costPriceCategoriesService';
import { cn } from '../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../styling/stylingHelper';
import { ColorPicker } from '../common/ColorPicker';
import { SortableList } from '../common/SortableList';

interface CategoryAssignmentModalProps {
  itemName: string;
  /** Header label when it differs from the stored name — a partner venue's row reads
   *  "<item> — Bar POP", and its stored name differs from the restaurant's own only in
   *  casing, so the raw name alone cannot tell the two rows apart. Defaults to itemName. */
  displayName?: string;
  currentCategoryId: string;
  categories: CostPriceCategory[];
  onSelectCategory: (categoryId: string) => Promise<void>;
  onCreateAndAssignCategory: (categoryName: string) => Promise<void>;
  onReorderCategories?: (reorderedCategories: CostPriceCategory[]) => Promise<void>;
  onClose: () => void;
  onCategoryUpdated?: () => Promise<void>;
}

interface SortableCategoryRowProps {
  category: CostPriceCategory;
  isCurrent: boolean;
  isEditingColor: boolean;
  editingColor: string;
  onSelect: () => void;
  onStartColorEdit: () => void;
  onColorChange: (color: string) => void;
  onCloseColorPicker: () => void;
  draggable?: boolean;
}

const SortableCategoryRow = ({
  category,
  isCurrent,
  isEditingColor,
  editingColor,
  onSelect,
  onStartColorEdit,
  onColorChange,
  onCloseColorPicker,
  draggable = true,
}: SortableCategoryRowProps) => {
  const sortable = useSortable({ id: category.id, disabled: !draggable || isEditingColor });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors border-b last:border-b-0",
        borderPrimary,
        isCurrent && "bg-emerald-50 dark:bg-darkGreenColor-dark/20"
      )}
    >
      {isEditingColor ? (
        <div className="flex flex-col gap-3">
          <ColorPicker value={editingColor} onChange={onColorChange} label="" />
          <button
            onClick={onCloseColorPicker}
            className={cn(
              "px-3 py-1.5 rounded-lg text-sm font-medium border",
              borderPrimary,
              whiteBG,
              "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors w-full"
            )}
          >
            Done
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {draggable && (
            <button
              {...attributes}
              {...listeners}
              className="touch-none cursor-grab active:cursor-grabbing flex-shrink-0"
              aria-label="Drag to reorder"
              type="button"
            >
              <GripVertical className="w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStartColorEdit();
            }}
            className="cursor-pointer hover:opacity-80 transition-opacity"
            title="Click to change color"
          >
            <div
              className="w-5 h-5 rounded-md shrink-0 border border-gray-200 dark:border-gray-700"
              style={{ backgroundColor: category.color || '#2563EB' }}
            />
          </button>
          <button onClick={onSelect} className="flex-1 text-left">
            <div className={cn(mediumText, baseTextSize, primaryText)}>{category.name}</div>
          </button>
          {isCurrent && <Check className="w-5 h-5 text-emerald-600 dark:text-textGreen-dark" />}
        </div>
      )}
    </div>
  );
};

const CategoryAssignmentModal = ({
  itemName,
  displayName,
  currentCategoryId,
  categories,
  onSelectCategory,
  onCreateAndAssignCategory,
  onReorderCategories,
  onClose,
  onCategoryUpdated,
}: CategoryAssignmentModalProps) => {
  const { t } = useTranslation();
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingColorCategoryId, setEditingColorCategoryId] = useState<string | null>(null);
  const [editingColor, setEditingColor] = useState<string>('');

  // Sort categories by position (non-default first, then default at bottom)
  const sortedCategories = [...categories].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return 1;
    if (!a.isDefault && b.isDefault) return -1;
    return a.position - b.position;
  });

  // Only non-default categories can be reordered
  const nonDefaultCategories = sortedCategories.filter(cat => !cat.isDefault);
  const defaultCategories = sortedCategories.filter(cat => cat.isDefault);

  const handleReorder = async (reordered: CostPriceCategory[]) => {
    if (!onReorderCategories) return;
    const updated = reordered.map((cat, index) => ({ ...cat, position: index }));
    try {
      await onReorderCategories([...updated, ...defaultCategories]);
    } catch (error) {
      console.error('Failed to reorder categories:', error);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    await onCreateAndAssignCategory(newCategoryName);
    setNewCategoryName('');
  };

  const handleStartColorEdit = (category: CostPriceCategory) => {
    setEditingColorCategoryId(category.id);
    setEditingColor(category.color);
  };

  const handleColorChange = async (categoryId: string, newColor: string) => {
    setEditingColor(newColor);
    try {
      await updateCostPriceCategory({
        id: categoryId,
        color: newColor,
      });
      if (onCategoryUpdated) {
        await onCategoryUpdated();
      }
    } catch (error) {
      console.error('Failed to update category color:', error);
      alert('Failed to update category color');
    }
  };

  const handleCloseColorPicker = () => {
    setEditingColorCategoryId(null);
    setEditingColor('');
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/60 flex items-center justify-center z-50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          "bg-white dark:bg-background-dark rounded-none sm:rounded-xl shadow-xl w-full max-w-lg border h-full sm:h-auto max-h-none sm:max-h-[90vh] flex flex-col",
          borderPrimary
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex-1 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start mb-4 relative">
            <div>
              <h2 className={cn(semiBoldText, largeTextSize, primaryText, "mb-1")}>
                {t('costPrice.categoryAssignmentModal.changeCategory')}
              </h2>
              <p className={cn(smallTextSize, secondaryText)}>
                "{displayName ?? itemName}"
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Category List */}
          <div className="mb-4 flex-1 overflow-hidden">
            <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
              {t('costPrice.categoryAssignmentModal.selectCategory')}
            </label>
            <div className={cn(
              "max-h-[450px] overflow-y-auto border rounded-xl pr-2",
              borderPrimary
            )}>
              {/* Non-default categories (draggable) */}
              {onReorderCategories ? (
                <SortableList
                  items={nonDefaultCategories}
                  getId={(cat) => cat.id}
                  onReorder={handleReorder}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                  {nonDefaultCategories.map((category) => (
                    <SortableCategoryRow
                      key={category.id}
                      category={category}
                      isCurrent={category.id === currentCategoryId}
                      isEditingColor={editingColorCategoryId === category.id}
                      editingColor={editingColor}
                      onSelect={() => onSelectCategory(category.id)}
                      onStartColorEdit={() => handleStartColorEdit(category)}
                      onColorChange={(newColor) => handleColorChange(category.id, newColor)}
                      onCloseColorPicker={handleCloseColorPicker}
                    />
                  ))}
                </SortableList>
              ) : (
                nonDefaultCategories.map((category) => (
                  <SortableCategoryRow
                    key={category.id}
                    category={category}
                    isCurrent={category.id === currentCategoryId}
                    isEditingColor={editingColorCategoryId === category.id}
                    editingColor={editingColor}
                    onSelect={() => onSelectCategory(category.id)}
                    onStartColorEdit={() => handleStartColorEdit(category)}
                    onColorChange={(newColor) => handleColorChange(category.id, newColor)}
                    onCloseColorPicker={handleCloseColorPicker}
                    draggable={false}
                  />
                ))
              )}

              {/* Default categories (not draggable) */}
              {defaultCategories.map(category => (
                <div
                  key={category.id}
                  className={cn(
                    "w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors border-b last:border-b-0",
                    borderPrimary,
                    category.id === currentCategoryId && "bg-emerald-50 dark:bg-darkGreenColor-dark/20"
                  )}
                >
                  {editingColorCategoryId === category.id ? (
                    // Color editing mode
                    <div className="flex flex-col gap-3">
                      <ColorPicker
                        value={editingColor}
                        onChange={(newColor) => handleColorChange(category.id, newColor)}
                        label=""
                      />
                      <button
                        onClick={handleCloseColorPicker}
                        className={cn(
                          "px-3 py-1.5 rounded-lg text-sm font-medium border",
                          borderPrimary,
                          whiteBG,
                          "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors w-full"
                        )}
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    // Normal display mode
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartColorEdit(category);
                        }}
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        title="Click to change color"
                      >
                        <div
                          className="w-5 h-5 rounded-md shrink-0 border border-gray-200 dark:border-gray-700"
                          style={{ backgroundColor: category.color || '#2563EB' }}
                        />
                      </button>
                      <button
                        onClick={() => onSelectCategory(category.id)}
                        className="flex-1 text-left"
                      >
                        <div className={cn(mediumText, baseTextSize, primaryText)}>{category.name}</div>
                        <div className={cn("text-xs mt-0.5", secondaryText)}>{t('costPrice.categoryAssignmentModal.defaultCategory')}</div>
                      </button>
                      {category.id === currentCategoryId && (
                        <Check className="w-5 h-5 text-emerald-600 dark:text-textGreen-dark" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Create New Category */}
          <div className="space-y-3 border-t pt-4 border-borderPrimary dark:border-borderPrimary-dark">
            <label className={cn(mediumText, baseTextSize, secondaryText, "block")}>
              {t('costPrice.categoryAssignmentModal.orCreateNewCategory')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={t('costPrice.categoryAssignmentModal.enterNewCategoryName')}
                className={cn(
                  "flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400",
                  borderPrimary,
                  whiteBG,
                  primaryText
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCategoryName.trim()) {
                    handleCreateCategory();
                  }
                }}
              />
              <button
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim()}
                className={cn(
                  "px-4 py-3 text-white dark:text-white rounded-xl transition flex items-center justify-center border",
                  "bg-darkGreenColor hover:bg-darkGreenColor/95 dark:bg-darkGreenColor-dark dark:hover:bg-darkGreenColor-dark/95",
                  borderPrimary,
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                <Check className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="-mx-4 mt-4">
            <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
            <div className="flex justify-end gap-3 pt-4 px-4">
              <button
                onClick={onClose}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  "rounded-xl px-4 py-3",
                  whiteBG,
                  "border",
                  borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                  "w-full"
                )}
              >
                {t('costPrice.categoryAssignmentModal.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryAssignmentModal;
